import React, { useState, ChangeEvent, useEffect, useCallback, useRef } from 'react';
import s from './PromtBlocks.module.css';
import Slider from '../../../../shared/ui/slider/Slider';
import Button from '../../../../shared/ui/button/Button';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../../../store/store';

import { updateImage, generateImage } from '../../../../shared/api/imageProjectApi';
import {
    selectSelectedImage,
    selectSelectedSlotIndex,
} from '../../../../store/selectors/imageSelectors';
import { selectImageProject } from '../../../../store/selectors/imageSelectors';
import { addImagesToLayout, deleteImageFromLayout } from '../../../../shared/api/imageLayoutApi';
import { toast } from 'react-toastify';
const ImagePromptBlock: React.FC = () => {
    const dispatch = useAppDispatch();

    const imageProject = useSelector(selectImageProject);
    const selectedImage = useSelector(selectSelectedImage);
    const selectedSlotIndex = useSelector(selectSelectedSlotIndex);

    const imageToShow = imageProject?.allImagesForLayouts?.find(
        (img) => img.id === selectedImage?.image?.id,
    );

    const [prompt, setPrompt] = useState(imageToShow?.prompt || '');
    const [realism, setRealism] = useState(imageToShow?.is_realism_mode || false);
    const [useReference, setUseReference] = useState(
        imageToShow?.reference_settings?.use_object || false,
    );
    const [referenceFile, setReferenceFile] = useState<File | null>(null);
    const [creativity, setCreativity] = useState(imageToShow?.creativity_level || 1);

    const [refSettings, setRefSettings] = useState({
        restyle: false,
        background: false,
        object: false,
        source: false,
    });

    useEffect(() => {
        setPrompt(imageToShow?.prompt || '');
        setRealism(imageToShow?.is_realism_mode || false);
        setUseReference(imageToShow?.reference_settings?.use_object || false);
        setCreativity(imageToShow?.creativity_level || 1);
    }, [imageToShow?.id]);

    const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setReferenceFile(file);
    };

    const toggleRefSetting = (key: keyof typeof refSettings) => {
        setRefSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleRealismChange = () => {
        setRealism((prev: boolean) => !prev);
        if (imageToShow?.id) {
            dispatch(
                updateImage({
                    imageId: imageToShow.id,
                    is_realism_mode: !realism,
                }),
            );
        }
    };

    const handleGenerate = async () => {
        if (!imageProject?.layouts?.length) return;

        try {
            const generated = await dispatch(
                generateImage({
                    prompt,
                    creativity_level: creativity,
                    is_realism_mode: realism,
                }),
            ).unwrap();

            let layoutId: string | null = null;
            let order: number | null = null;

            if (selectedImage?.image != null) {
                layoutId = selectedImage.layoutId;
                const layout = imageProject.layouts.find((l) => l.id === layoutId);
                const imgMeta =
                    selectedImage.image &&
                    layout?.images?.find((img) => img.id === selectedImage.image!.id);
                order = imgMeta?.meta.order ?? 1;

                await dispatch(
                    deleteImageFromLayout({
                        layoutId: layoutId!,
                        imageId: selectedImage.image.id,
                    }),
                ).unwrap();

                await dispatch(
                    addImagesToLayout({
                        layoutId: layoutId!,
                        images: [{ id: generated.id, order: selectedImage.image.order ?? 1 }],
                    }),
                ).unwrap();
            } else if (selectedSlotIndex?.slotIndex && selectedSlotIndex.layoutId) {
                layoutId = selectedSlotIndex.layoutId;
                order = selectedSlotIndex.slotIndex;
            }

            if (layoutId && order && generated?.id) {
                await dispatch(
                    addImagesToLayout({
                        layoutId,
                        images: [{ id: generated.id, order }],
                    }),
                ).unwrap();
            }

            toast.success('Image generated successfully');
        } catch (error: any) {
            toast.error(error?.message || 'Error generating image');
            console.error('Error generating image:', error);
        }
    };

    if (!selectedImage?.image && !selectedSlotIndex?.slotIndex)
        return <p className={s.text}>No Image or Slot selected</p>;

    return (
        <div className={s.container}>
            <div className={s.block_container}>
                <p className={s.title}>Imagine anything with a single prompt</p>

                <textarea
                    className={s.input}
                    placeholder="Create a post about the marketing budget and how it will shape the future…"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                />

                <label className={s.checkbox_label}>
                    <input type="checkbox" checked={realism} onChange={handleRealismChange} />
                    Enable realism mode
                </label>

                {/* <label className={s.checkbox_label}>
                    <input
                        type="checkbox"
                        checked={useReference}
                        onChange={handleReferenceChange}
                    />
                    Upload own image as reference
                </label> */}
                {useReference && (
                    <Button
                        onClick={() => document.getElementById('ref-input')!.click()}
                        text={'Upload Image'}
                        style={{
                            backgroundColor: '#FFDD35',
                            borderRadius: '4px',
                            color: '#000',
                            width: '132px',
                            height: '28px',
                            margin: '0 auto',
                            marginBottom: '10px',
                        }}
                    />
                )}
                <input
                    id="ref-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onFileChange}
                />

                {referenceFile && useReference && (
                    <div className={s.preview}>
                        <img
                            src={URL.createObjectURL(referenceFile)}
                            alt="reference preview"
                            className={s.preview_img}
                        />
                    </div>
                )}
            </div>

            {useReference && (
                <div className={s.block_container}>
                    <p className={s.title}>Reference settings</p>
                    <label className={s.checkbox_label}>
                        <input
                            type="checkbox"
                            checked={refSettings.restyle}
                            onChange={() => toggleRefSetting('restyle')}
                        />
                        Restyle entire image
                    </label>
                    <label className={s.checkbox_label}>
                        <input
                            type="checkbox"
                            checked={refSettings.background}
                            onChange={() => toggleRefSetting('background')}
                        />
                        Use background
                    </label>
                    <label className={s.checkbox_label}>
                        <input
                            type="checkbox"
                            checked={refSettings.object}
                            onChange={() => toggleRefSetting('object')}
                        />
                        Use object on reference
                    </label>
                    <label className={s.checkbox_label}>
                        <input
                            type="checkbox"
                            checked={refSettings.source}
                            onChange={() => toggleRefSetting('source')}
                        />
                        Use source on reference
                    </label>
                </div>
            )}

            <div className={s.block_container}>
                <p className={s.title}>Adjust creativity level</p>
                <Slider value={creativity} onChange={setCreativity} min={1} max={3} />
                <div className={s.value_container}>
                    <span className={s.label}>1</span>
                    <span className={s.label}>2</span>
                    <span className={s.label}>3</span>
                </div>
                <p className={s.tip}>
                    Tip: Higher levels are more creative but less obedient to the prompt
                </p>
                <Button
                    text="Generate"
                    style={{
                        backgroundColor: '#FFDD35',
                        borderRadius: '4px',
                        width: '132px',
                        margin: '0 auto',
                        marginBottom: '10px',
                    }}
                    onClick={handleGenerate}
                />
            </div>
        </div>
    );
};

export default ImagePromptBlock;
